/**
 * O que o EDITOR acrescenta ao renderer — e nada disso existe em produção.
 *
 * A inversão que este arquivo representa é o ponto principal da Fase 3.
 *
 * Hoje há um canvas de 2.556 linhas que faz os dois papéis, decidindo em dezenas
 * de lugares se está editando ou exibindo (`if (viewMode === 'edit')`). O
 * resultado é que a tela publicada carrega, executa e paga por todo o código de
 * edição — overlays, seleção, arraste, `document.querySelector`, `setTimeout` —
 * e que um bug de edição pode alcançar a tela do cliente.
 *
 * Aqui é ao contrário: o renderer não sabe que existe editor. Quem monta a tela
 * PROVÊ este token; quem só exibe, não. `inject(EDITOR_CHROME, { optional: true })`
 * devolve `null` em produção, e o código de decoração nem roda.
 */
import { InjectionToken, Type } from '@angular/core';

import { NodeType, ScreenNode } from '../model/node-props';

/** Onde o indicador de inserção aparece, em relação ao nó sob o cursor. */
export type DropPosition = 'before' | 'after' | 'inside';

export interface DropTargetSpec {
  /** O nó aceita receber algo? */
  readonly accepts: boolean;
  /** Posições válidas para este alvo. */
  readonly positions: readonly DropPosition[];
  /** Chave de tradução do motivo, quando não aceita — vira o texto do cursor. */
  readonly rejectionKey?: string;
}

export interface EditorChrome {
  /**
   * Classes a acrescentar no elemento do nó: selecionado, sob o cursor,
   * inválido, com comportamento. Devolve um mapa para o template usar `ngClass`
   * sem alocar objeto por ciclo de detecção.
   */
  decorate(node: ScreenNode): Readonly<Record<string, boolean>>;

  /** Componente de sobreposição do nó — alças, barra de ação. `null` = nenhum. */
  overlayFor(node: ScreenNode): Type<unknown> | null;

  /**
   * Chance de o editor consumir o evento antes do componente.
   * Devolver `true` significa "eu tratei, não deixe passar" — é o que impede
   * que clicar num select dentro do canvas abra a lista em vez de selecionar o nó.
   */
  interceptPointer(node: ScreenNode, event: PointerEvent): boolean;

  /** Regras de soltura deste nó, para o arraste. `null` = não é alvo. */
  dropTargetFor(node: ScreenNode): DropTargetSpec | null;

  /**
   * Pedido de seleção sem gesto de ponteiro.
   *
   * Existe para o caso em que a parte clicável de um nó fica FORA do invólucro
   * dele: o título de uma aba vive na barra do `nz-tabset`, não no conteúdo, e
   * sem isto a aba seria a única parte da tela impossível de selecionar quando
   * o conteúdo dela estivesse cheio.
   *
   * Em produção o token não existe e nada disto é chamado.
   */
  requestSelect(node: ScreenNode): void;

  /**
   * Pedido de inserção vindo do próprio container.
   *
   * Existe para a ação que só faz sentido DENTRO de um componente: o "+" da
   * barra de abas. A alternativa era oferecer `tab` na paleta, e o resultado
   * foi um catálogo com "Abas" e "Aba" lado a lado — o usuário clicava no que
   * via e ganhava um container novo, fora do que estava editando.
   *
   * Em produção o token não existe e nada disto é chamado.
   */
  requestInsert(parent: ScreenNode, type: NodeType): void;

  /**
   * Tipos que o editor quer desenhar de forma diferente no canvas.
   *
   * Existe para os casos em que renderizar de verdade no editor é caro ou
   * inconveniente: um `dashboardEmbed` dentro do canvas dispararia as consultas
   * do dashboard a cada montagem, e um `customComponent` executaria código de
   * terceiro no meio da edição. Nesses casos o editor devolve um marcador.
   */
  placeholderFor(type: NodeType): Type<unknown> | null;
}

export const EDITOR_CHROME = new InjectionToken<EditorChrome>('EDITOR_CHROME');
